import {useQuery} from "@tanstack/react-query";
import {getProducts} from "../../api/catalog";
import {useContext, useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {ApiRequestInterface} from "../../interfaces/catalog/ApiRequestInterface";
import {formatBoldText} from "../../utils/formatBoldText";
import {ProductInterface} from "../../interfaces/catalog/ProductInterface";
import {CategoryInterface} from "../../interfaces/catalog/CategoryInterface";
import Product from "./Product";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {GlobalContext, ReducerActionType} from "../../GlobalReducer";
import BreadcrumbInterface from "../../interfaces/core/BreadcrumbInterface";
import {sortProductsByStock} from "../../utils/productSort";

interface GroupedProperties {
    [propertyName: string]: {
        [productId: number]: string[];
    };
}

enum PropertyType {
    ALL = 'all',
    DIFFERENCES = 'differences'
}

// TODO: на беке добавить комплектации
// TODO: доавить количество товара в различиях

export default function Comparison() {
    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const [searchParams] = useSearchParams();

    const sharedIds = useMemo(() => {
        const ids = searchParams.get('ids');
        if (!ids) {
            return null;
        }
        return ids.split(',').map(id => id.trim()).filter(id => id.length > 0);
    }, [searchParams]);

    const isSharedView = sharedIds !== null;

    const [params, setParams] = useState<ApiRequestInterface>({})
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [propertiesType, setPropertyType] = useState<PropertyType>(PropertyType.ALL);
    const [linkCopied, setLinkCopied] = useState(false);

    const shareLink = useMemo(() => {
        const ids = state.comparison.map(product => product.id.toString()).join(',');
        return `https://b2b.pride.ru/comparison?ids=${ids}`;
    }, [state.comparison]);

    const handleCopyShareLink = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const {data: response, status} = useQuery({
        queryKey: [params, state.customer?.id],
        queryFn: () => getProducts(params, state.customer),
        //@ts-ignore
        retry: !window.NOSCRIPT ? false : 1,
        enabled: !!params.id && params.id.length > 0
    })

    const  uniquePropertiesDictionary = useMemo((): Record<string, string> => {
        const dictionary: Record<string, string> = {};

        response?.products.forEach(product => {
            Object.entries(product.properties_dictionary).forEach(([key, value]) => {
                dictionary[key] = value.name;
            });
        });
        return dictionary;
    }, [response?.products]);

    useEffect(() => {
        let breadcrumbs: BreadcrumbInterface[] = [];

        breadcrumbs.push({
            name: 'Сравнение товаров',
        })
        dispatch({type: ReducerActionType.SET_BREADCRUMBS, payload: breadcrumbs})
    }, []);

    const groupedByCategory = useMemo(() => {
        const grouped: Record<string, { category: CategoryInterface; products: ProductInterface[] }> = {};

        response?.products.forEach(product => {
            const categoryId = product.category.catalog_code;
            if (!grouped[categoryId]) {
                grouped[categoryId] = {
                    category: product.category,
                    products: []
                };
            }
            grouped[categoryId].products.push(product);
        });

        return grouped;
    }, [response?.products]);

    const filteredProducts = useMemo(() => {
        let products: ProductInterface[] = [];

        if (selectedCategoryId === null) {
            products = response?.products || [];
        } else {
            products = groupedByCategory[selectedCategoryId]?.products || [];
        }

        return sortProductsByStock(products);
    }, [selectedCategoryId, groupedByCategory, response?.products]);

    const groupProductsByProperties = useMemo((): GroupedProperties => {
        const grouped: GroupedProperties = {};

        filteredProducts?.forEach(product => {
            Object.entries(product.properties).forEach(([propertyId, values]) => {
                if(!uniquePropertiesDictionary[propertyId]){
                    return;
                }
                if (!grouped[propertyId]) {
                    grouped[propertyId] = {};
                }
                if(values.filter(value => value !== '').length === 0){
                    return;
                }
                grouped[propertyId][product.id] = values;
            });
        });

        if (propertiesType === PropertyType.DIFFERENCES) {
            const filteredProductIds = filteredProducts?.map(product => product.id) ?? [];

            // если выбрано меньше 2 товаров — пропускаем
            if (filteredProductIds.length < 2) {
                return grouped;
            }

            Object.keys(grouped).forEach(propertyId => {
                let productValues = Object.entries(grouped[propertyId])
                    .filter(([productId]) => filteredProductIds.includes(Number(productId)))
                    .map(([_, values]) => {
                        // Приводим к строке для сравнения
                        return Array.isArray(values) ? values.join("|") : String(values);
                    });

                const productValuesBeforeFilterCount  = productValues.length;

                productValues = Array.from(new Set(productValues))

                if (productValues.length <= 1 && productValuesBeforeFilterCount > 1 ) {
                    delete grouped[propertyId];
                }
            });
        }

        return grouped;
    }, [filteredProducts, propertiesType]);

    const removeCategory = (categoryId: string) => {
        if (isSharedView) {
            return;
        }
        dispatch({type: ReducerActionType.SET_COMPARISON, payload: response?.products.filter(product => product.category.catalog_code !== categoryId)});
    };

    useEffect(() => {
        setParams({id: isSharedView ? sharedIds : state.comparison.map(product => product.id.toString())});
    }, [state.comparison, isSharedView, sharedIds]);

    const handleCategoryClick = (categoryId: string) => {
        if (selectedCategoryId !== categoryId) {
            setSelectedCategoryId(categoryId);
        }
    };

    if(!isSharedView && state.comparison.length === 0){
        return <h1 className={'text-center my-5 py-5'}>В сравнении пока ничего нет</h1>
    }

    if(isSharedView && sharedIds.length === 0){
        return <h1 className={'text-center my-5 py-5'}>В сравнении пока ничего нет</h1>
    }

    return (<div className="compar">
        <div className="container">
            <h1 className="compar__title">
                Сравнение товаров
            </h1>
            <div className="compar__head">
                <div className="d-flex flex-wrap compar__head-row">
                    {Object.entries(groupedByCategory).map(([categoryId, {category, products: categoryProducts}]) => (
                        <div className={"pointer compar__points " + (selectedCategoryId === categoryId && 'gray')} key={categoryId}>
                            <span onClick={() => handleCategoryClick(categoryId)}>{formatBoldText(category.name)} ({categoryProducts.length})</span>
                            {!isSharedView && <div className="close" onClick={() => removeCategory(categoryId)}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 11L1 1M11 1L1 11" stroke="#292929" strokeLinecap="round"></path>
                                </svg>
                            </div>}
                        </div>
                    ))}
                </div>
                <div className="feedback__left-bot">
                    <div className="d-flex flex-column">
                        <label className="item-radio">
                            <input checked={propertiesType===PropertyType.ALL} type="radio" value="Все характеристикиу" name="connection"
                                   onClick={() => setPropertyType(PropertyType.ALL)}
                                   className="item-radio__input"/>
                            <div className="item-radio__icon"></div>
                            <span>Все характеристики</span><span className="t-gray">({Object.entries(groupProductsByProperties).length})</span>
                        </label>
                        <label className="item-radio">
                            <input checked={propertiesType===PropertyType.DIFFERENCES} type="radio" value="Только различия" name="connection"
                                   onClick={() => setPropertyType(PropertyType.DIFFERENCES)}
                                   className="item-radio__input"/>
                            <div className="item-radio__icon"></div>
                            <span>Только различия</span><span className="t-gray"></span>
                        </label>
                    </div>
                </div>
            </div>
            <div className="compar__body">
                <div className="compar__row-card">
                    <div className="prod-card">

                    </div>
                    {filteredProducts?.map(product => <div className="prod-card" key={product.id}>
                        <Product product={product} />
                    </div>)}
                </div>
                <div className="d-flex flex-column compar__descript">
                    <div className="d-flex flex-column compar__descript-row">
                        <div className="compar__item-row title-row">
                            <div className="compar__item fw-bold title">
                                Характеристики
                            </div>
                        </div>
                        {Object.entries(groupProductsByProperties).map(([propertyId, productValues]) => (
                            <div className="compar__item-row" key={propertyId}>
                                <div className="compar__item label">
                                    {uniquePropertiesDictionary[propertyId]}
                                </div>
                                {filteredProducts?.map(product => (
                                    <div className="compar__item" key={product.id}>
                                        {productValues[product.id] || '-'}
                                    </div>
                                ))}
                            </div>
                        ))}
                        <div className="compar__item-row" >
                            <div className="compar__item label">
                                Размер
                            </div>
                            {filteredProducts?.map(product => (
                                <div className="compar__item" key={product.id}>
                                    {product.width} x {product.height} x {product.length} мм
                                </div>
                            ))}
                        </div>
                        <div className="compar__item-row" >
                            <div className="compar__item label">
                                Вес
                            </div>
                            {filteredProducts?.map(product => (
                                <div className="compar__item" key={product.id}>
                                    {product.weight} кг
                                </div>
                            ))}
                        </div>
                        {filteredProducts?.find(product => product.additional_params?.specification_list_raw?.length > 0) && <div className="compar__item-row" >
                            <div className="compar__item label">
                                Комплектация
                            </div>
                            {filteredProducts?.map(product => (
                                <div className="compar__item" key={product.id}>
                                    <ul>
                                    {product.additional_params.specification_list_raw?.map((item: string, index: number  ) => <li key={index}>{item}</li>)}
                                    </ul>
                                </div>
                            ))}
                        </div>}
                    </div>
                </div>
            </div>
            {!isSharedView && (
                <div className=" align-items-center flex-wrap gap-2 text-center">
                    <div><b>Ссылка на это сравнение: </b>{shareLink}</div>
                    <div className={'mt-3'}>
                        <button type="button" className={!linkCopied ? 'btn btn-secondary' : 'btn btn-danger'} onClick={handleCopyShareLink}>
                            {linkCopied ? 'Скопировано' : 'Скопировать'}
                        </button>
                    </div>
                </div>
            )}
        </div>

    </div>)
}